import React from "react";
import moment from "moment";
import cns from "classnames";
import { compact } from "lodash";
import { Grid, withStyles } from "@material-ui/core";

import AccountIcon from "@material-ui/icons/AccountCircle";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ContactIcon from "@material-ui/icons/PermContactCalendar";
import EmailIcon from "@material-ui/icons/Email";
import LayersIcon from "@material-ui/icons/Layers";
import MedicalRecordIcon from "@material-ui/icons/VideogameAsset";
import PaymentIcon from "@material-ui/icons/Payment";
import PeopleIcon from "@material-ui/icons/People";
import PhoneIcon from "@material-ui/icons/PhoneInTalk";
import ScheduleIcon from "@material-ui/icons/Schedule";

import locale from "@bonliva-crm/locale";
import { ThemeType, WithStyles } from "@theme";

import IconTitle from "./IconTitle";
import Body from "../typography/Body";
import { PlainLink } from "../typography/PlainLink";
import { WeekRanges } from "../WeekRanges";
import UserAvatar from "../../containers/UserAvatar";
import { WeekRangeProps } from "../WeekRange";

type GridSettings = Record<"lg" | "md" | "xs", { min: number; max: number }>;
type Props = {
  items: React.ReactNode[];
  gridSettings?: Partial<GridSettings>;
} & WithStyles<typeof styles>;

const styles = (theme: ThemeType) => ({
  root: {
    padding: `${theme.spacing()}px ${theme.spacing(2)}px`
  },

  cardDetail: {
    paddingTop: 5,
    paddingBottom: 5
  }
});

const perRow = 12;
const defaultGridSettings: GridSettings = {
  lg: { min: 3, max: 6 },
  md: { min: 4, max: 6 },
  xs: { min: 5, max: 6 }
};
const calculateGridSettings = (gridSettings: GridSettings, length: number) =>
  Object.keys(gridSettings).reduce((p, c) => {
    const setting = gridSettings[c];
    const rows = Math.ceil((length * setting.min) / perRow);
    const space = rows * perRow;
    const spacePerItem = Math.min(Math.floor(space / length), setting.max);
    p[c] = spacePerItem;
    return p;
  }, {});

export const CardDetails: React.FC<Props> = (props) => {
  const { classes, items, gridSettings = {} } = props;
  const grid = calculateGridSettings(
    { ...defaultGridSettings, ...gridSettings },
    items.length - 1
  );

  return (
    <Grid item container className={cns(classes.root, "highlighter")}>
      {compact(items).map((cell: React.ReactNode, i) => (
        <Grid key={i} item {...grid} className={classes.cardDetail}>
          {cell}
        </Grid>
      ))}
    </Grid>
  );
};

CardDetails.defaultProps = {
  items: []
};

export default withStyles(styles)(CardDetails);

export const emailToDetail = (
  primaryEmail: React.ReactNode,
  secondaryEmail?: React.ReactNode
) => {
  const emails = [primaryEmail, secondaryEmail].filter(Boolean);
  const icon = <EmailIcon fontSize="small" />;
  const title = locale.Email;

  return emails.length ? (
    emails.map((email, idx) => (
      <IconTitle key={idx} icon={icon} title={title}>
        <PlainLink copy href={`mailto:${email}`} variant="body2">
          {email}
        </PlainLink>
      </IconTitle>
    ))
  ) : (
    <IconTitle icon={icon} title={title} children="" />
  );
};

export const phoneToDetails = (phone: React.ReactNode) => (
  <IconTitle icon={<PhoneIcon fontSize="small" />} title={locale.Phone}>
    {phone && (
      <PlainLink copy href={`tel:${phone}`} variant="body2">
        {phone}
      </PlainLink>
    )}
  </IconTitle>
);

export const lastContactToDetail = (lastContactDate: Date) => {
  const m = moment.utc(lastContactDate);
  return (
    <IconTitle icon={<ContactIcon fontSize="small" />} title={locale.LastContact}>
      <Body data-test="LastContactDate" variant="body2">
        {lastContactDate ? (
          <time test-data={m.toDate().getTime()}>{m.format("L")}</time>
        ) : (
          "n/a"
        )}
      </Body>
    </IconTitle>
  );
};

export const responsibleUserToDetail = (props: { PersonId: string; Name: string }) =>
  props && (
    <IconTitle
      icon={props ? <UserAvatar userName={props.Name} size="mini" /> : <AccountIcon />}
      title={locale["Responsible BA"]}
      variant="body2"
    >
      {props.Name}
    </IconTitle>
  );

export const ssnToDetail = (ssn: React.ReactNode) => (
  <IconTitle
    icon={<MedicalRecordIcon fontSize="small" />}
    title={locale["Personal number"]}
  >
    <PlainLink copy href={`${ssn}`} variant="body2">
      {ssn}
    </PlainLink>
  </IconTitle>
);

export const claimToDetail = (value: string, title: string) =>
  value && (
    <IconTitle icon={<PaymentIcon fontSize="small" />} title={title} variant="body2">
      {value} kr/tim
    </IconTitle>
  );

export const WEEKS_NUM = 3;

export const availabilityToDetail = (ranges: WeekRangeProps[]) => (
  <IconTitle
    icon={
      <ScheduleIcon
        fontSize="small"
        style={{ marginTop: ranges.length > WEEKS_NUM ? -21 : null }}
      />
    }
    title={locale.Period}
    variant="body2"
  >
    <WeekRanges ranges={ranges.slice(0, WEEKS_NUM)} hasMore={ranges.length > WEEKS_NUM} />
  </IconTitle>
);

export const vacanciesToDetail = (total: number | string) => (
  <IconTitle
    icon={<PeopleIcon fontSize="small" />}
    title={locale.Vacancies2}
    variant="body2"
  >
    {total}
  </IconTitle>
);

export const busyRatioToDetail = (percentage: number | string) => (
  <IconTitle
    icon={<LayersIcon fontSize="small" />}
    title={locale.BusyRatio}
    variant="body2"
  >
    {percentage}
    {" %"}
  </IconTitle>
);

export const weeksToDetail = (content: React.ReactNode) => (
  <IconTitle
    icon={<CalendarTodayIcon fontSize="small" style={{ fontSize: 20 }} />}
    title={locale.Period}
    variant="body2"
  >
    {content}
  </IconTitle>
);

export const prescriptionCodeToDetail = (value: string, title: string) =>
  value && (
    <IconTitle icon={<PaymentIcon fontSize="small" />} title={title} variant="body2">
      <PlainLink copy href={value} variant="body2">
        {value}
      </PlainLink>
    </IconTitle>
  );
